// admin-view/components/knowledge-base/services/knowledge.service.ts

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  KnowledgeCategory,
  KnowledgeSection,
  KnowledgeArticle,
  KnowledgeBlock,
  UploadResponse,
} from '../interfaces/knowledge.interface';

export interface FeatureSuggestionDto {
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high';
}

@Injectable({
  providedIn: 'root',
})
export class KnowledgeService {
  private baseUrl = `${environment.apiUrl}/knowledge`;

  constructor(private http: HttpClient) {}

  // ==================== PUBLIC (for help panel) ====================

  getByRoute(route: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/by-route`, { params: { route } });
  }

  getArticle(id: number): Observable<KnowledgeArticle> {
    return this.http.get<KnowledgeArticle>(`${this.baseUrl}/article/${id}`);
  }

  searchArticles(query: string): Observable<KnowledgeArticle[]> {
    return this.http.get<KnowledgeArticle[]>(`${this.baseUrl}/search`, { params: { q: query } });
  }

  // ==================== ADMIN - Categories ====================

  getAllCategories(): Observable<KnowledgeCategory[]> {
    return this.http.get<KnowledgeCategory[]>(`${this.baseUrl}/admin/categories`);
  }

  createCategory(data: Partial<KnowledgeCategory>): Observable<KnowledgeCategory> {
    return this.http.post<KnowledgeCategory>(`${this.baseUrl}/admin/category`, data);
  }

  updateCategory(id: number, data: Partial<KnowledgeCategory>): Observable<KnowledgeCategory> {
    return this.http.put<KnowledgeCategory>(`${this.baseUrl}/admin/category/${id}`, data);
  }

  deleteCategory(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/admin/category/${id}`);
  }

  // ==================== ADMIN - Sections ====================

  getSections(categoryId: number): Observable<KnowledgeSection[]> {
    return this.http.get<KnowledgeSection[]>(`${this.baseUrl}/admin/sections/${categoryId}`);
  }

  createSection(data: Partial<KnowledgeSection>): Observable<KnowledgeSection> {
    return this.http.post<KnowledgeSection>(`${this.baseUrl}/admin/section`, data);
  }

  updateSection(id: number, data: Partial<KnowledgeSection>): Observable<KnowledgeSection> {
    return this.http.put<KnowledgeSection>(`${this.baseUrl}/admin/section/${id}`, data);
  }

  deleteSection(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/admin/section/${id}`);
  }

  // ==================== ADMIN - Articles ====================

  getArticles(sectionId: number): Observable<KnowledgeArticle[]> {
    return this.http.get<KnowledgeArticle[]>(`${this.baseUrl}/admin/articles/${sectionId}`);
  }

  getArticleAdmin(id: number): Observable<KnowledgeArticle> {
    return this.http.get<KnowledgeArticle>(`${this.baseUrl}/admin/article/${id}`);
  }

  createArticle(data: Partial<KnowledgeArticle>): Observable<KnowledgeArticle> {
    return this.http.post<KnowledgeArticle>(`${this.baseUrl}/admin/article`, data);
  }

  updateArticle(id: number, data: Partial<KnowledgeArticle>): Observable<KnowledgeArticle> {
    return this.http.put<KnowledgeArticle>(`${this.baseUrl}/admin/article/${id}`, data);
  }

  deleteArticle(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/admin/article/${id}`);
  }

  // ==================== ADMIN - Blocks ====================

  createBlock(data: Partial<KnowledgeBlock>): Observable<KnowledgeBlock> {
    return this.http.post<KnowledgeBlock>(`${this.baseUrl}/admin/block`, data);
  }

  updateBlock(id: number, data: Partial<KnowledgeBlock>): Observable<KnowledgeBlock> {
    return this.http.put<KnowledgeBlock>(`${this.baseUrl}/admin/block/${id}`, data);
  }

  deleteBlock(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/admin/block/${id}`);
  }

  reorderBlocks(articleId: number, blockIds: number[]): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/admin/blocks/reorder`, { articleId, blockIds });
  }

  // ==================== ADMIN - Upload ====================

  uploadFile(file: File): Observable<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<UploadResponse>(`${this.baseUrl}/admin/upload`, formData);
  }
  
  // Add this method to KnowledgeService class:
  submitSuggestion(data: FeatureSuggestionDto): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(`${this.baseUrl}/suggestion`, data);
  }
}